import { useEffect } from "react";
import type { Solicitud } from "@/types";
import EstadoBadge from "@/pages/admin/components/EstadoBadge";
import { estadosSolicitud } from "@/lib/solicitudEstados";
import { formatFecha, formatFechaHora, phoneToWhatsapp } from "@/lib/format";

interface SolicitudDetalleProps {
  solicitud: Solicitud;
  updating: boolean;
  onClose: () => void;
  onEstadoChange: (id: string, estado: string) => void;
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5 border-b border-background-200 py-3 last:border-b-0 sm:flex-row sm:gap-4">
      <dt className="w-full text-xs font-label uppercase tracking-wide text-foreground-500 sm:w-40 sm:shrink-0">
        {label}
      </dt>
      <dd className="text-sm text-foreground-800">{value}</dd>
    </div>
  );
}

export default function SolicitudDetalle({
  solicitud,
  updating,
  onClose,
  onEstadoChange,
}: SolicitudDetalleProps) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const whatsapp = phoneToWhatsapp(solicitud.telefono);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Cerrar detalle"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-foreground-950/40"
      ></button>

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Solicitud ${solicitud.numero}`}
        className="relative flex h-full w-full max-w-xl flex-col bg-background-50"
      >
        <header className="flex items-start justify-between gap-3 border-b border-background-200 px-5 py-4">
          <div>
            <p className="text-xs font-label uppercase tracking-[0.14em] text-foreground-500">
              Solicitud
            </p>
            <h2 className="mt-0.5 text-2xl font-heading font-bold text-primary-700">
              {solicitud.numero}
            </h2>
            <div className="mt-2">
              <EstadoBadge estado={solicitud.estado} size="md" />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl leading-none" aria-hidden="true"></i>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* Estado */}
          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <label
              htmlFor="estado-select"
              className="mb-1.5 block text-xs font-label font-semibold uppercase tracking-wide text-foreground-600"
            >
              Cambiar estado
            </label>
            <div className="flex items-center gap-3">
              <select
                id="estado-select"
                value={solicitud.estado}
                onChange={(event) => onEstadoChange(solicitud.id, event.target.value)}
                disabled={updating}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:opacity-60"
              >
                {estadosSolicitud.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {updating && (
                <i
                  className="ri-loader-4-line animate-spin text-lg leading-none text-primary-500"
                  aria-label="Guardando"
                ></i>
              )}
            </div>
          </div>

          {/* Acciones de contacto */}
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <a
              href={`tel:${solicitud.telefono}`}
              className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-3 py-2.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-phone-line text-base leading-none" aria-hidden="true"></i>
              Llamar
            </a>
            <a
              href={`mailto:${solicitud.email}`}
              className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-3 py-2.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-mail-line text-base leading-none" aria-hidden="true"></i>
              Correo
            </a>
            {whatsapp ? (
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-3 py-2.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                <i className="ri-whatsapp-line text-base leading-none" aria-hidden="true"></i>
                WhatsApp
              </a>
            ) : null}
          </div>

          {/* Cliente */}
          <section className="mt-6">
            <h3 className="text-sm font-heading font-semibold text-foreground-950">
              Datos del cliente
            </h3>
            <dl className="mt-2">
              <DetailRow label="Nombre" value={solicitud.nombre_cliente} />
              <DetailRow label="Empresa" value={solicitud.empresa} />
              <DetailRow label="Teléfono" value={solicitud.telefono} />
              <DetailRow label="Correo" value={solicitud.email} />
            </dl>
          </section>

          {/* Servicio */}
          <section className="mt-6">
            <h3 className="text-sm font-heading font-semibold text-foreground-950">
              Detalles del servicio
            </h3>
            <dl className="mt-2">
              <DetailRow label="Servicio" value={solicitud.tipo_servicio} />
              <DetailRow label="Unidad" value={solicitud.unidad} />
              <DetailRow label="Fecha" value={solicitud.fecha ? formatFecha(solicitud.fecha) : null} />
              <DetailRow label="Hora" value={solicitud.hora} />
              <DetailRow label="Origen" value={solicitud.origen} />
              <DetailRow label="Destino" value={solicitud.destino} />
              <DetailRow
                label="Personas"
                value={solicitud.personas ? String(solicitud.personas) : null}
              />
              <DetailRow label="Tipo de carga" value={solicitud.tipo_carga} />
              <DetailRow label="Asesoría" value={solicitud.requiere_asesoria ? "Sí, requiere asesoría" : null} />
            </dl>
          </section>

          {solicitud.descripcion && (
            <section className="mt-6">
              <h3 className="text-sm font-heading font-semibold text-foreground-950">Descripción</h3>
              <p className="mt-2 whitespace-pre-line rounded-md border border-background-200 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
                {solicitud.descripcion}
              </p>
            </section>
          )}

          {solicitud.info_adicional && (
            <section className="mt-6">
              <h3 className="text-sm font-heading font-semibold text-foreground-950">
                Información adicional
              </h3>
              <p className="mt-2 whitespace-pre-line rounded-md border border-background-200 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
                {solicitud.info_adicional}
              </p>
            </section>
          )}

          <div className="mt-6 flex flex-col gap-1 border-t border-background-200 pt-4 text-xs text-foreground-500">
            <span>Registrada el {formatFechaHora(solicitud.creado_en)}</span>
            {solicitud.actualizado_en && <span>Última actualización: {formatFechaHora(solicitud.actualizado_en)}</span>}
          </div>
        </div>
      </aside>
    </div>
  );
}